import classNames from "classnames";
import { PropsWithChildren, ReactNode, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslations } from "../../hooks/useTranslations";
import SVGBackArrow from "../../icons/BackArrow.svg?react";
import Alert from "./Alert";
import Icon from "./Icon";
import LoadingSpinner from "./LoadingSpinner";
import styles from "./Page.module.css";

const maintenance = {
  de: `Aufgrund von Wartungsarbeiten sind einige Anwendungen am Mittwoch, den 23. September, von 20:00 Uhr bis voraussichtlich 23:00 Uhr nicht verfügbar.`,
  it: `A causa di lavori di manutenzione, alcune applicazioni non saranno disponibili mercoledì 23 settembre, dalle ore 20:00 alle ore 23:00 circa.`,
};

type Props = PropsWithChildren<{
  title: string;
  shortInfo?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  backLinkText?: string;
  isLoading?: boolean;
  narrow?: boolean;
}>;

const Page = ({
  title,
  shortInfo,
  description,
  actions,
  backLinkText,
  isLoading = false,
  narrow = false,
  children,
}: Props) => {
  const { i18n, currentLanguage } = useTranslations();
  useEffect(() => {
    document.title = `${title} | ${i18n.title}`;
  }, [title, i18n]);

  return (
    <div className={classNames(styles.page, { [styles.narrow]: narrow })}>
      <div>
        <Alert showUntil={new Date(2026, 8, 23, 23, 0)}>
          {maintenance[currentLanguage]}
        </Alert>
        {backLinkText && (
          <NavLink className={styles.backLink} to="..">
            <Icon glyph={SVGBackArrow} className={styles.icon} />
            {backLinkText}
          </NavLink>
        )}
        <header className={styles.header}>
          <div className={styles.heading}>
            {title.length > 0 && <h1 className={styles.title}>{title}</h1>}
            {actions && <div className={styles.actions}>{actions}</div>}
          </div>
          {shortInfo && <div className={styles.shortInfo}>{shortInfo}</div>}
          {description && (
            <div className={styles.description}>{description}</div>
          )}
        </header>
      </div>

      {!isLoading ? (
        children
      ) : (
        <div className={styles.loadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Page;
